import { AllCardsType } from '../../../data/all-cards'
import { NombreTarotista } from '../../../data/personality'
import { ComboWizardData } from '../shared/combo-wizard-props'
import { buildComboPreamble, ComboPreviousStep } from '../../../utils/combo-context'
import { formatCardLabel } from '../../../utils/card-label'
import {
    AMOR_MERECIMIENTO_NAME,
    AMOR_MERECIMIENTO_PREAMBLE,
    AMOR_MERECIMIENTO_STEPS,
    AMOR_MERECIMIENTO_TOTAL_STEPS,
} from './combo-config'

const cardLabel = (card: AllCardsType) => formatCardLabel(card)

const consultaExtra = (data: ComboWizardData) =>
    data.consulta.length > 0
        ? 'ten en cuenta en la tirada la siguiente consulta que realiza el consultante: ' +
          data.consulta +
          ','
        : ''

const buildLoveSolterosPrompt = (cards: AllCardsType[], data: ComboWizardData): string => {
    const extra =
        data.consulta.length > 0
            ? 'Responde las inquietudes del consultante usando exclusivamente las cartas extraídas: ' +
              data.consulta
            : ''

    return `Por favor, realiza la interpretación de la *** Tirada del Amor *** de 10 cartas que realizaste para ${
        data.consultante
    }, que es una persona soltera o sin pareja, de la siguiente forma: 1. Energía actual en el amor: ${cardLabel(
        cards[0]
    )}, 2. Bloqueos emocionales que impiden el amor: ${cardLabel(cards[1])}, 3. Cualidades atractivas del consultante: ${cardLabel(
        cards[2]
    )}, 4. Patrón de relaciones pasadas que debe sanar: ${cardLabel(cards[3])}, 5. Consejo sobre cómo abrirse al amor: ${cardLabel(
        cards[4]
    )}, 6. Posibilidades de conocer a alguien en el corto plazo: ${cardLabel(cards[5])}, 7. Cómo reconocer a la persona indicada: ${cardLabel(
        cards[6]
    )}, 8. Obstáculo o advertencia en el camino: ${cardLabel(cards[7])}, 9. Hacia donde se dirige la situación amorosa: ${cardLabel(
        cards[8]
    )} y 10. Resultado a mediano plazo: ${cardLabel(cards[9])}. ${extra} Dirígete directamente al consultante.`
}

const buildStepPrompt = (step: number, cards: AllCardsType[], data: ComboWizardData): string => {
    const extra = consultaExtra(data)

    if (step === 1) {
        return buildLoveSolterosPrompt(cards, data)
    }

    if (step === 2) {
        return `Quiero que interpretes una **Tirada de Amor Propio y Merecimiento** con 10 cartas, ayudando al consultante ${
            data.consultante
        } a sanar su relación consigo mismo/a, liberar bloqueos de merecimiento y abrirse a recibir amor y cuidado, ${extra} según estas cartas: **Relación actual con el amor propio — Estado actual (¿cómo te estás tratando a vos mismo/a hoy? ¿Hay autocrítica, descuido o hay algo de cuidado?):** ${cardLabel(
            cards[0]
        )}, **Principal bloque de merecimiento — Bloque principal (¿cuál es la creencia o sensación más fuerte que te impide sentirte merecedor/a?):** ${cardLabel(
            cards[1]
        )}, **Origen del bloque — Raíz (¿de dónde viene esa dificultad para sentirte merecedor/a? infancia, familia, relaciones pasadas):** ${cardLabel(
            cards[2]
        )}, **Cómo se manifiesta en tu vida — Manifestación actual (¿de qué forma concreta te está afectando este bloque hoy en amor, dinero, oportunidades o vínculos?):** ${cardLabel(
            cards[3]
        )}, **Qué te estás negando — Privación actual (¿qué amor, cuidado o experiencia te estás privando por no sentirte merecedor/a?):** ${cardLabel(
            cards[4]
        )}, **Miedo que hay detrás — Miedo raíz (¿qué miedo aparece cuando pensás en recibir más amor o cuidado?):** ${cardLabel(
            cards[5]
        )}, **Qué tenés que soltar — Liberación (¿qué creencia, patrón o forma de relacionarte con vos mismo/a necesitás dejar ir?):** ${cardLabel(
            cards[6]
        )}, **Qué necesitás activar / cultivar — Activación (¿qué cualidad o forma de amarte necesitás desarrollar o permitir?):** ${cardLabel(
            cards[7]
        )}, **Acción concreta de amor propio — Paso accionable (¿qué podés hacer de forma práctica y amorosa para empezar a sentirte más merecedor/a?):** ${cardLabel(
            cards[8]
        )} y **Mensaje de tu Yo Superior / Alma — Mensaje sanador y cierre (¿qué quiere recordarte tu parte más sabia sobre tu merecimiento?):** ${cardLabel(
            cards[9]
        )}. Explica detalladamente cada carta y su significado en la posición donde ha salido. Teje una lectura coherente sobre el amor propio y el merecimiento: conecta cómo te tratás hoy, el bloque principal, su origen, cómo se manifiesta, lo que te negás y el camino hacia la liberación y la activación. Detecta patrones de autocrítica, miedos al merecimiento y oportunidades de autocuidado. Proporciona consejos prácticos, tiernos y concretos para cultivar el amor propio. Mantén un tono profesional, empático, sanador y comprensible. Dirígete directamente al consultante, no utilices la tercera persona refiriéndote al consultante, aún cuando las consultas se encuentren en tercera persona, adapta la respuesta para que quede en segunda persona. Incluye además en 2 párrafos después de la lectura, una respuesta directa para el consultante y un consejo final (extra-tarot).`
    }

    return `Quiero que interpretes una **Tirada de Conexión con tu Yo Futuro** con 10 cartas, ayudando al consultante ${
        data.consultante
    } a conectar con la versión de sí mismo/a que puede llegar a ser en los próximos 1 a 3 años en el ámbito del amor y las relaciones, ${extra} según estas cartas: **Energía actual del consultante:** ${cardLabel(
        cards[0]
    )}, **Lo que más está pidiendo atención ahora:** ${cardLabel(cards[1])}, **Energía y presencia del Yo Futuro (quién se convierte esta persona dentro de 1 a 3 años; enfócate en su identidad, energía, presencia y cualidades principales en el amor):** ${cardLabel(
        cards[2]
    )}, **Cómo se siente emocionalmente esa versión futura:** ${cardLabel(cards[3])}, **Qué está creando o viviendo esa versión futura en el amor:** ${cardLabel(
        cards[4]
    )}, **Qué necesita soltar para llegar ahí:** ${cardLabel(cards[5])}, **Qué necesita cultivar o activar:** ${cardLabel(
        cards[6]
    )}, **Apoyo invisible o guías que ya tiene disponible:** ${cardLabel(cards[7])}, **El paso más poderoso y realista que puede dar ahora:** ${cardLabel(
        cards[8]
    )} y **Mensaje final de su Yo Futuro + visión del camino amoroso si sigue esta línea:** ${cardLabel(
        cards[9]
    )}. Explica detalladamente cada carta y su significado en la posición donde ha salido. Conecta el presente con el yo futuro en clave amorosa: muestra cómo la energía actual, lo que pide atención y lo que debe soltar o cultivar se relacionan con la identidad amorosa que revela el yo futuro. Detecta patrones, bloqueos y oportunidades en la lectura. Proporciona consejos prácticos, realistas y motivadores para acercarse a esa versión futura. Mantén un tono profesional, empático, esperanzador y comprensible. Dirígete directamente al consultante, no utilices la tercera persona refiriéndote al consultante, aún cuando las consultas se encuentren en tercera persona, adapta la respuesta para que quede en segunda persona. Incluye además en 2 párrafos después de la lectura, una respuesta directa para el consultante y un consejo final (extra-tarot).`
}

export const interpretStep = (
    step: number,
    cards: AllCardsType[],
    data: ComboWizardData,
    user: string,
    personality: NombreTarotista,
    previousSteps: ComboPreviousStep[],
    comboId: string
) => {
    const config = AMOR_MERECIMIENTO_STEPS[step - 1]
    const preamble = buildComboPreamble(
        AMOR_MERECIMIENTO_NAME,
        step,
        AMOR_MERECIMIENTO_TOTAL_STEPS,
        previousSteps,
        AMOR_MERECIMIENTO_PREAMBLE
    )
    const consult = preamble + buildStepPrompt(step, cards, data)

    return {
        consult,
        user,
        personality,
        clientName: data.consultante.name,
        clientPhone: data.consultante.phone || '',
        clientAnswer: data.consulta,
        cards,
        type: config.type,
        comboId,
        comboName: AMOR_MERECIMIENTO_NAME,
        comboStep: step,
        comboTotalSteps: AMOR_MERECIMIENTO_TOTAL_STEPS,
        ...(step === 1 && data.tipo ? { loveTipo: data.tipo } : {}),
    }
}
